import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import {
    adminOverview,
    adminUsers,
    adminUploads,
    adminReports,
    adminTransactions,
    adminContacts,
    adminQuotes,
    adminEmailOptins,
    adminAuditLogs,
    adminRetryExtraction,
    adminRegenerateReport,
    adminFeedbackTriage,
    adminSupportOutstandingCount,
    adminMarkContactStatus,
    adminPreflightSmokeTest,
    adminPreflightHistory,
    adminTestDriftAlert,
    adminGitStatus,
    adminBillingTestStart,
    adminBillingTestRecent,
} from "@/lib/api";
import CmsArticles from "@/components/admin/CmsArticles";
import NewsletterConversionDashboard from "@/components/admin/NewsletterConversionDashboard";
import AdminContentPackages from "@/pages/admin/AdminContentPackages";
import SupportInbox from "@/components/admin/SupportInbox";
import OpenAIUsage from "@/components/admin/OpenAIUsage";
import QuotesAdmin from "@/components/admin/QuotesAdmin";
import DeletionRequestsAdmin from "@/components/admin/DeletionRequestsAdmin";
import StripeEventsAdmin from "@/components/admin/StripeEventsAdmin";
import CostCalibrationAdmin from "@/components/admin/CostCalibrationAdmin";
import AdminFeedback from "@/components/admin/AdminFeedback";
import SupportSettings from "@/components/admin/SupportSettings";
import AdminTools from "@/components/admin/AdminTools";
import DayZeroReset from "@/components/admin/DayZeroReset";
import HealthTripwiresPanel from "@/components/admin/HealthTripwiresPanel";
import HeroRegeneration from "@/components/admin/HeroRegeneration";
import LaunchFunnel from "@/components/admin/LaunchFunnel";
import StuckUsersPanel from "@/components/admin/StuckUsersPanel";
import LeadCapturesPanel from "@/components/admin/LeadCapturesPanel";
import TestFeedbackScrubPanel from "@/components/admin/TestFeedbackScrubPanel";
import UrlSourcesPanel from "@/components/admin/UrlSourcesPanel";
import WcagAuditPanel from "@/components/admin/WcagAuditPanel";
import OpsStatusTile from "@/components/admin/OpsStatusTile";
import RestoreFromR2 from "@/components/admin/RestoreFromR2";
import PipelineHealth from "@/components/admin/PipelineHealth";
import ContentJobsTile from "@/components/admin/ContentJobsTile";
import R2MirrorStatus from "@/components/admin/R2MirrorStatus";
import PinRendererHealth from "@/components/admin/PinRendererHealth";
import { useAuth } from "@/lib/auth";
import { Navigate, Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { RotateCcw, RefreshCw, AlertTriangle, Camera } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, testid }) {
    return (
        <div className="bl-card p-5" data-testid={testid}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                {label}
            </div>
            <div className="mt-2 font-display font-semibold text-3xl text-bl tnum">
                {value}
            </div>
        </div>
    );
}


const GITHUB_DISMISS_KEY = "bl_admin_github_reminder_dismissed_at";

function SaveToGithubBanner() {
    const [info, setInfo] = useState(null);
    // Hydrate dismissed-state synchronously from localStorage during the
    // initial render so we don't trigger React Compiler's
    // set-state-in-effect rule.
    const [dismissed, setDismissed] = useState(() => {
        try {
            const last = parseInt(localStorage.getItem(GITHUB_DISMISS_KEY) || "0", 10);
            return (Date.now() - last) / 36e5 < 24;
        } catch {
            return false;
        }
    });

    const load = async () => {
        try {
            const r = await adminGitStatus();
            setInfo(r);
        } catch {
            /* non-blocking */
        }
    };

    useEffect(() => { load(); }, []);

    if (dismissed || !info?.ok) return null;

    const ageMin = Math.floor((info.age_seconds || 0) / 60);
    const human =
        ageMin < 1 ? "just now" :
        ageMin < 60 ? `${ageMin} min ago` :
        ageMin < 60 * 24 ? `${Math.floor(ageMin / 60)} h ago` :
        `${Math.floor(ageMin / (60 * 24))} d ago`;

    const handleDismiss = () => {
        try {
            localStorage.setItem(GITHUB_DISMISS_KEY, String(Date.now()));
        } catch {
            // localStorage blocked — banner dismisses for this session only.
        }
        setDismissed(true);
    };

    return (
        <div
            className="mt-6 flex flex-wrap items-center justify-between gap-3 p-3 rounded-md border border-[var(--bl-amber)] bg-[var(--bl-amber-tint)]"
            data-testid="admin-github-reminder"
        >
            <div className="flex items-start gap-3">
                <span aria-hidden className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-[var(--bl-amber)] text-white text-[11px] font-semibold items-center justify-center">!</span>
                <div className="text-sm text-bl leading-snug">
                    <b>Last code change {human}</b>
                    {info.last_commit_message ? (
                        <span className="text-bl-muted"> — “{info.last_commit_message.slice(0, 80)}”</span>
                    ) : null}
                    <div className="text-bl-muted text-[12px] mt-0.5">
                        Before you deploy, use the <b>Save to GitHub</b> button in the chat input to push the latest auto-commits to your connected repo.
                    </div>
                </div>
            </div>
            <button
                type="button"
                onClick={handleDismiss}
                className="bl-btn-ghost text-[11px] px-2 py-1 shrink-0"
                data-testid="admin-github-reminder-dismiss"
            >
                Dismiss for 24h
            </button>
        </div>
    );
}


function BillingTestButton() {
    const [busy, setBusy] = useState(false);
    const [last, setLast] = useState(null);

    const loadLast = async () => {
        try {
            const r = await adminBillingTestRecent();
            setLast((r?.tests || [])[0] || null);
        } catch { /* non-critical */ }
    };

    useEffect(() => { loadLast(); }, []);

    const run = async () => {
        if (busy) return;
        if (!window.confirm(
            "This opens Stripe Checkout for a $1 LIVE charge on your real card. "
            + "Stripe webhook will auto-refund the moment it's confirmed. Proceed?"
        )) return;
        setBusy(true);
        try {
            const r = await adminBillingTestStart();
            if (r?.url) {
                window.open(r.url, "_blank", "noopener,noreferrer");
                toast.info("Stripe Checkout opened in a new tab. Complete the $1 payment — webhook will auto-refund.");
                // Poll for the test result for 60s
                let attempts = 0;
                const poll = setInterval(async () => {
                    attempts += 1;
                    try {
                        const r2 = await adminBillingTestRecent();
                        const newest = (r2?.tests || [])[0];
                        if (newest && (!last || newest.tested_at !== last.tested_at)) {
                            setLast(newest);
                            clearInterval(poll);
                            if (newest.ok) {
                                toast.success("Billing test ✓ — charge captured, auto-refund complete.");
                            } else {
                                toast.warning("Webhook fired but refund failed — check Stripe Dashboard.");
                            }
                        }
                    } catch { /* non-critical */ }
                    if (attempts > 30) clearInterval(poll); // 60s timeout
                }, 2000);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to start billing test");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col items-end gap-1">
            <button
                type="button"
                onClick={run}
                disabled={busy}
                className="bl-btn-ghost text-xs"
                title="Live $1 Stripe charge that the webhook auto-refunds — verifies end-to-end payment loop"
                data-testid="admin-billing-test-run"
            >
                {busy ? "Opening Stripe…" : "$1 billing test"}
            </button>
            {last && (
                <div
                    className="text-[10px] text-bl-muted"
                    data-testid="admin-billing-test-last"
                    title={`session=${last.session_id || ""} intent=${last.intent_id || ""} refund=${last.refund_id || ""}`}
                >
                    Last: {last.ok ? "✓" : "!"} {(last.tested_at || "").slice(0, 16).replace("T", " ")}
                </div>
            )}
        </div>
    );
}

function PreflightSmokeTest() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const [driftBusy, setDriftBusy] = useState(false);
    const [driftOpen, setDriftOpen] = useState(false);
    const [driftTripwires, setDriftTripwires] = useState({ preflight: true, backup: true, stripe: true });
    const [driftDirection, setDriftDirection] = useState("other_red");
    const loadHistory = async () => {
        try {
            const r = await adminPreflightHistory(7);
            setHistory(r?.snapshots || []);
        } catch {
            /* non-blocking */
        }
    };

    useEffect(() => { loadHistory(); }, []);

    const run = async () => {
        setBusy(true);
        try {
            const r = await adminPreflightSmokeTest();
            setResult(r);
            loadHistory();
            if (r.ok) {
                toast.success(`Pre-flight passed · ${r.passed}/${r.total}`);
            } else {
                toast.warning(`Pre-flight: ${r.passed}/${r.total} checks passed`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Smoke test failed to run");
        } finally {
            setBusy(false);
        }
    };

    const runDriftTest = async () => {
        setDriftBusy(true);
        const tripwires = Object.entries(driftTripwires)
            .filter(([, v]) => v)
            .map(([k]) => k);
        try {
            const r = await adminTestDriftAlert({ tripwires, direction: driftDirection });
            if (r.delivered) {
                toast.success(`Drift alert sent to ${r.recipient} · ${r.tripwire_count} tripwire(s)`);
            } else {
                toast.info(`Drift alert ${r.send_mode === "sandbox" ? "sandboxed" : "suppressed"} · ${r.tripwire_count} tripwire(s) — event logged for ${r.recipient}`);
            }
            setDriftOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Drift-alert test failed");
        } finally {
            setDriftBusy(false);
        }
    };

    const toggleTripwire = (key) => {
        setDriftTripwires((prev) => ({ ...prev, [key]: !prev[key] }));
    };
    const anyTripwireSelected = Object.values(driftTripwires).some(Boolean);

    // Build 7-day sparkline left-to-right (oldest → newest). Missing
    // days render as a neutral grey cell so streak gaps are visible.
    const today = new Date();
    const sparkDays = Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(today);
        d.setUTCDate(today.getUTCDate() - (6 - i));
        const key = d.toISOString().slice(0, 10);
        const snap = history.find((s) => s.date === key);
        return { key, snap };
    });

    return (
        <div className="bl-card p-5 mt-6" data-testid="admin-preflight">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                        Pre-flight smoke test
                    </div>
                    <div className="mt-1 text-sm text-bl-muted">
                        One-click prod-readiness check: Mongo, env vars, DB write, support-email path, Metricool, send mode.
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex flex-col items-end gap-1" data-testid="admin-preflight-sparkline">
                        <div className="text-[9px] tracking-[0.18em] uppercase text-bl-muted">
                            Last 7 days
                        </div>
                        <div className="flex gap-0.5">
                            {sparkDays.map(({ key, snap }) => {
                                const cls = !snap
                                    ? "bg-bl-bg border-bl"
                                    : snap.ok
                                        ? "bg-[var(--bl-moss)] border-[var(--bl-moss)]"
                                        : "bg-[var(--bl-rust)] border-[var(--bl-rust)]";
                                const title = snap
                                    ? `${key} · ${snap.ok ? "OK" : "FAIL"} (${snap.passed}/${snap.total})`
                                    : `${key} · no snapshot`;
                                return (
                                    <span
                                        key={key}
                                        title={title}
                                        className={`w-3.5 h-4 border rounded-sm ${cls}`}
                                        data-testid={`admin-preflight-spark-${key}`}
                                    />
                                );
                            })}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={run}
                        disabled={busy}
                        className="bl-btn-secondary"
                        data-testid="admin-preflight-run"
                    >
                        {busy ? "Running…" : result ? "Re-run" : "Run pre-flight"}
                    </button>
                    <Popover open={driftOpen} onOpenChange={setDriftOpen}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="bl-btn-ghost text-xs"
                                title="Send a configurable synthetic cross-env drift alert email to yourself."
                                data-testid="admin-test-drift-alert"
                            >
                                Test drift email
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="end"
                            className="w-72 p-3 text-sm"
                            data-testid="admin-test-drift-alert-popover"
                        >
                            <div className="font-display font-semibold text-bl mb-2 text-xs uppercase tracking-wider">
                                Force-drift simulation
                            </div>
                            <div className="text-[10px] text-bl-muted mb-2 uppercase tracking-wider">Tripwires</div>
                            <div className="flex flex-col gap-1.5 mb-3">
                                {[
                                    { k: "preflight", label: "Preflight checks" },
                                    { k: "backup", label: "Backup health" },
                                    { k: "stripe", label: "Stripe webhooks" },
                                ].map(({ k, label }) => (
                                    <label
                                        key={k}
                                        className="flex items-center gap-2 text-xs text-bl cursor-pointer"
                                        data-testid={`drift-tripwire-${k}-label`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!driftTripwires[k]}
                                            onChange={() => toggleTripwire(k)}
                                            data-testid={`drift-tripwire-${k}`}
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>
                            <div className="text-[10px] text-bl-muted mb-2 uppercase tracking-wider">Direction</div>
                            <div className="flex flex-col gap-1.5 mb-3">
                                {[
                                    { v: "other_red", label: "Other env failing (this healthy)" },
                                    { v: "this_red", label: "This env failing (other healthy)" },
                                ].map(({ v, label }) => (
                                    <label
                                        key={v}
                                        className="flex items-center gap-2 text-xs text-bl cursor-pointer"
                                    >
                                        <input
                                            type="radio"
                                            name="drift-direction"
                                            value={v}
                                            checked={driftDirection === v}
                                            onChange={(e) => setDriftDirection(e.target.value)}
                                            data-testid={`drift-direction-${v}`}
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={runDriftTest}
                                disabled={driftBusy || !anyTripwireSelected}
                                className="bl-btn-secondary w-full text-xs"
                                data-testid="admin-test-drift-alert-send"
                            >
                                {driftBusy ? "Sending…" : "Send test email"}
                            </button>
                            {!anyTripwireSelected && (
                                <div className="mt-2 text-[10px] text-bl-muted text-center">
                                    Pick at least one tripwire to send.
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>
                    <BillingTestButton />
                </div>
            </div>
            {result && (
                <div
                    className={
                        "mt-4 border rounded-md p-3 "
                        + (result.ok
                            ? "bg-[var(--bl-moss-tint)] border-[var(--bl-moss)]"
                            : "bg-[var(--bl-rust-tint)] border-[var(--bl-rust)]")
                    }
                    data-testid="admin-preflight-result"
                >
                    <div className="font-display font-semibold text-bl">
                        {result.ok ? "All systems go" : "Action required"} · {result.passed}/{result.total}
                    </div>
                    <ul className="mt-2 space-y-1 text-sm">
                        {result.results.map((r, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-2"
                                data-testid={`admin-preflight-step-${i}`}
                            >
                                <span
                                    className={
                                        "mt-0.5 inline-block w-3.5 h-3.5 rounded-full text-[10px] font-semibold text-white text-center leading-[14px] "
                                        + (r.ok ? "bg-[var(--bl-moss)]" : "bg-[var(--bl-rust)]")
                                    }
                                    aria-hidden
                                >
                                    {r.ok ? "✓" : "!"}
                                </span>
                                <span className="text-bl">
                                    <b>{r.step}</b>
                                    <span className="text-bl-muted"> — {r.detail}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function Table({ rows, columns, testid }) {
    return (
        <div className="bl-card overflow-x-auto" data-testid={testid}>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left">
                        {columns.map((c) => (
                            <th
                                key={c.key}
                                className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl"
                            >
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td
                                colSpan={columns.length}
                                className="px-5 py-8 text-center text-bl-muted"
                            >
                                No data
                            </td>
                        </tr>
                    )}
                    {rows.map((r, i) => (
                        <tr key={i} className="border-b border-bl last:border-0">
                            {columns.map((c) => (
                                <td
                                    key={c.key}
                                    className="px-5 py-3 text-bl align-top"
                                >
                                    {c.render
                                        ? c.render(r[c.key], r)
                                        : String(r[c.key] ?? "—")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function Admin() {
    const { user, loading } = useAuth();
    const [overview, setOverview] = useState(null);
    const [users, setUsers] = useState([]);
    const [uploads, setUploads] = useState([]);
    const [reports, setReports] = useState([]);
    const [txns, setTxns] = useState([]);
    const [contacts, setContacts] = useState([]);
    // Real-data filter — admin defaults to showing real customers only,
    // not the testers (`@example.com`, `tester@…`, …) accumulated during
    // QA. Persisted to localStorage so the choice survives reloads.
    const [includeTest, setIncludeTest] = useState(() => {
        try {
            return localStorage.getItem("bl_admin_include_test") === "1";
        } catch { return false; }
    });
    useEffect(() => {
        try {
            localStorage.setItem(
                "bl_admin_include_test", includeTest ? "1" : "0",
            );
        } catch { /* non-critical */ }
    }, [includeTest]);
    const [quotes, setQuotes] = useState([]);
    const [quoteCounts, setQuoteCounts] = useState({ new: 0, routed: 0, contacted: 0, won: 0, lost: 0 });
    const [quoteFilter, setQuoteFilter] = useState({ status: "", zip_code: "" });
    const [optins, setOptins] = useState({ opted_in: [], count: 0 });
    const [auditLogs, setAuditLogs] = useState([]);
    const [err, setErr] = useState(null);
    // Surfaces an `N aging` badge on the Feedback tab so admins notice
    // backlogs without having to visit the panel. Cost-safe — backend
    // route is pure DB aggregation, no AI.
    const [triageCount, setTriageCount] = useState(0);
    const [highSeverityCount, setHighSeverityCount] = useState(0);
    // Support inbox outstanding count — drives the red badge on the
    // "Support" tab trigger. Tickets in {new, reviewing, waiting_on_user}
    // + contact messages with no `read_at` timestamp.
    const [supportOutstanding, setSupportOutstanding] = useState({
        tickets_open: 0,
        contacts_unread: 0,
        total: 0,
    });

    const refetchAll = async () => {
        try {
            const opts = { include_test: includeTest };
            const [o, u, up, rp, tx, ct, qt, op, al] = await Promise.all([
                adminOverview(opts),
                adminUsers(undefined, opts),
                adminUploads(),
                adminReports(opts),
                adminTransactions(opts),
                adminContacts(opts),
                adminQuotes(quoteFilter),
                adminEmailOptins(),
                adminAuditLogs(),
            ]);
            setOverview(o);
            setUsers(u);
            setUploads(up);
            setReports(rp);
            setTxns(tx);
            setContacts(ct);
            setQuotes(qt.quotes || []);
            setQuoteCounts(qt.counts || { new: 0, routed: 0, contacted: 0, won: 0, lost: 0 });
            setOptins(op);
            setAuditLogs(al);
        } catch (e) {
            setErr("Failed to load admin data.");
        }
    };

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        refetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, quoteFilter.status, quoteFilter.zip_code, includeTest]);

    // Light triage-count poll. Cheap, pure DB aggregation; surfaces an
    // `N aging` badge on the Feedback tab so admins notice backlogs.
    useEffect(() => {
        if (!user || user.role !== "admin") return;
        let alive = true;
        const tick = async () => {
            try {
                const r = await adminFeedbackTriage(7, 1);
                if (!alive) return;
                setTriageCount(r?.total || 0);
                setHighSeverityCount(r?.high_severity_count || 0);
            } catch {
                /* silent — non-blocking */
            }
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => { alive = false; clearInterval(id); };
    }, [user]);

    // Support outstanding-count poll. Same cheap pattern as triage —
    // pure DB count_documents, no AI, no joins.
    useEffect(() => {
        if (!user || user.role !== "admin") return;
        let alive = true;
        const tick = async () => {
            try {
                const r = await adminSupportOutstandingCount({ include_test: includeTest });
                if (!alive) return;
                setSupportOutstanding({
                    tickets_open: r?.tickets_open || 0,
                    contacts_unread: r?.contacts_unread || 0,
                    total: r?.total || 0,
                });
            } catch {
                /* silent — non-blocking */
            }
        };
        tick();
        const id = setInterval(tick, 30_000);
        return () => { alive = false; clearInterval(id); };
    }, [user, includeTest]);

    const handleRetryExtraction = async (uploadId) => {
        try {
            await adminRetryExtraction(uploadId);
            toast.success("Extraction marked for retry.");
            refetchAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Retry failed.");
        }
    };

    const handleRegenerate = async (reportId) => {
        if (!window.confirm("Regenerate this report? This consumes AI credits.")) return;
        try {
            await adminRegenerateReport(reportId);
            toast.success("Report regenerated.");
            refetchAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Regeneration failed.");
        }
    };

    if (loading) {
        return (
            <PublicLayout>
                <div className="bl-container py-24 text-center text-bl-muted">
                    <div className="bl-spinner mx-auto mb-4" /> Loading…
                </div>
            </PublicLayout>
        );
    }
    if (!user) return <Navigate to="/auth/login?next=/admin" replace />;
    if (user.role !== "admin") {
        return (
            <PublicLayout>
                <div className="bl-container py-24">
                    <h1 className="font-display text-3xl text-bl">Admin only</h1>
                    <p className="mt-3 text-bl-muted">
                        This area is reserved for administrators.
                    </p>
                </div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <section className="bl-container py-12" data-testid="admin-page">
                <div className="bl-eyebrow">Admin</div>
                <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
                    <h1 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-bl">
                        Operational overview
                    </h1>
                    <Link
                        to="/admin/issues"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 border-[var(--bl-rust)]/30 bg-[var(--bl-rust)]/5 text-sm font-display font-semibold text-[var(--bl-rust)] hover:bg-[var(--bl-rust)]/10 hover:border-[var(--bl-rust)]/50 transition-colors"
                        data-testid="admin-link-issues"
                        title="View consolidated failure signals across the platform — take a screenshot for triage."
                    >
                        <AlertTriangle className="h-4 w-4" />
                        Issues & failures
                        <Camera className="h-3.5 w-3.5 opacity-70" />
                    </Link>
                </div>
                {err && <p className="text-[var(--bl-rust)] mt-4">{err}</p>}

                {/* Unified ops-status pill — single "is anything broken?"
                    glance check sitting above every other dashboard
                    section. Auto-refreshes every 60s. */}
                <OpsStatusTile />

                {/* Real-data filter — defaults to ON so launch metrics are
                    trustworthy at a glance. Click to flip the whole admin
                    dashboard between "real customers only" and "everything". */}
                <div
                    className="mt-4 flex items-center gap-3 text-sm"
                    data-testid="admin-real-data-toggle"
                >
                    <button
                        type="button"
                        role="switch"
                        aria-checked={!includeTest}
                        onClick={() => setIncludeTest((v) => !v)}
                        className={
                            "relative inline-flex items-center h-6 w-11 rounded-full transition-colors "
                            + (!includeTest
                                ? "bg-[var(--bl-teal)]"
                                : "bg-bl-line"
                            )
                        }
                        data-testid="admin-real-data-toggle-btn"
                    >
                        <span
                            className={
                                "inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform "
                                + (!includeTest ? "translate-x-6" : "translate-x-1")
                            }
                        />
                    </button>
                    <div className="text-bl-muted">
                        <span className="font-display font-semibold text-bl">
                            {!includeTest ? "Real data only" : "Showing test + real"}
                        </span>
                        {overview?.test_filter?.test_accounts_hidden > 0 && !includeTest && (
                            <>
                                {" · "}
                                <span className="text-xs">
                                    {overview.test_filter.test_accounts_hidden} test account
                                    {overview.test_filter.test_accounts_hidden === 1 ? "" : "s"} hidden
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {overview?.first_customer_milestone && (
                    <div
                        className="mt-4 rounded-xl border-2 border-[var(--bl-moss)] bg-gradient-to-br from-[var(--bl-moss-tint)] to-[var(--bl-teal-tint)] p-5"
                        data-testid="admin-first-customer-badge"
                    >
                        <div className="flex items-start gap-4">
                            <div className="text-3xl shrink-0" aria-hidden>🏆</div>
                            <div className="min-w-0 flex-1">
                                <div className="bl-eyebrow text-[var(--bl-moss)]">
                                    {overview.growth_metrics
                                        ? "Growth streak · since first customer"
                                        : "Milestone · first paying customer"}
                                </div>
                                <div className="mt-1 font-display font-semibold text-xl text-bl">
                                    {overview.first_customer_milestone.customer_name || "—"}
                                    <span className="text-bl-muted font-normal text-sm ml-2">
                                        {overview.first_customer_milestone.customer_email}
                                    </span>
                                </div>
                                <div className="mt-2 text-sm text-bl-muted flex flex-wrap gap-x-4 gap-y-1">
                                    {overview.first_customer_milestone.amount != null && (
                                        <span>
                                            <b className="text-[var(--bl-moss)]">
                                                ${(overview.first_customer_milestone.amount / 100).toFixed(2)}{" "}
                                                {(overview.first_customer_milestone.currency || "usd").toUpperCase()}
                                            </b>
                                        </span>
                                    )}
                                    {overview.first_customer_milestone.tier && (
                                        <span>
                                            Tier: <b className="text-bl">{String(overview.first_customer_milestone.tier).toUpperCase()}</b>
                                        </span>
                                    )}
                                    {overview.first_customer_milestone.fired_at && (
                                        <span>
                                            Fired:{" "}
                                            <b className="text-bl">
                                                {new Date(overview.first_customer_milestone.fired_at).toLocaleString()}
                                            </b>
                                        </span>
                                    )}
                                </div>

                                {/* Growth streak — only when at least one
                                    real paid transaction has actually
                                    landed. Pure read-only summary; no LLM,
                                    no joins, refreshes every overview reload. */}
                                {overview.growth_metrics && (
                                    <div
                                        className="mt-4 pt-3 border-t border-[var(--bl-moss)]/30 grid grid-cols-2 md:grid-cols-4 gap-3"
                                        data-testid="admin-growth-streak"
                                    >
                                        <div>
                                            <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                                                Streak
                                            </div>
                                            <div className="mt-1 text-2xl font-display font-bold text-[var(--bl-moss)] tabular-nums">
                                                {overview.growth_metrics.current_streak_days}
                                                <span className="text-sm font-normal text-bl-muted ml-1">
                                                    day{overview.growth_metrics.current_streak_days === 1 ? "" : "s"}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                                                Customers
                                            </div>
                                            <div className="mt-1 text-2xl font-display font-bold text-bl tabular-nums">
                                                {overview.growth_metrics.total_customers}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                                                Revenue
                                            </div>
                                            <div className="mt-1 text-2xl font-display font-bold text-[var(--bl-teal)] tabular-nums">
                                                ${(overview.growth_metrics.total_revenue_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                                                Best day
                                            </div>
                                            <div className="mt-1 text-2xl font-display font-bold text-bl tabular-nums">
                                                ${(overview.growth_metrics.best_day.amount_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </div>
                                            <div className="text-[10px] text-bl-muted mt-0.5">
                                                {overview.growth_metrics.best_day.date}
                                                {" · "}
                                                {overview.growth_metrics.best_day.customers} cust.
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Sparkline — last 7 days of revenue.
                                    Pure CSS bars, no chart library
                                    dependency. Tallest bar = best day in
                                    the 7-day window. */}
                                {overview.growth_metrics?.last_7_days?.length > 0 && (() => {
                                    const days = overview.growth_metrics.last_7_days;
                                    const peak = Math.max(
                                        1,
                                        ...days.map((d) => d.amount_cents),
                                    );
                                    return (
                                        <div
                                            className="mt-3 flex items-end gap-1 h-12"
                                            data-testid="admin-growth-sparkline"
                                            aria-label="Last 7 days revenue sparkline"
                                        >
                                            {days.map((d) => (
                                                <div
                                                    key={d.date}
                                                    className="flex-1 flex flex-col items-center gap-1"
                                                    title={`${d.date} · $${(d.amount_cents / 100).toFixed(0)} · ${d.customers} cust.`}
                                                >
                                                    <div className="w-full flex-1 flex items-end">
                                                        <div
                                                            className={d.amount_cents > 0
                                                                ? "w-full bg-[var(--bl-moss)] rounded-sm"
                                                                : "w-full bg-bl-line rounded-sm"}
                                                            style={{
                                                                height: `${Math.max(3, (d.amount_cents / peak) * 100)}%`,
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {overview && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <Stat label="Users" value={overview.users} testid="stat-users" />
                        <Stat label="Uploads" value={overview.uploads} testid="stat-uploads" />
                        <Stat label="Reports" value={overview.reports} testid="stat-reports" />
                        <Stat
                            label="Paid"
                            value={overview.paid_transactions}
                            testid="stat-paid"
                        />
                        <Stat
                            label="Failed Uploads"
                            value={overview.failed_uploads}
                            testid="stat-failed"
                        />
                        <Stat
                            label="Active Subs"
                            value={overview.active_subscriptions}
                            testid="stat-active-subs"
                        />
                    </div>
                )}

                <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-quick-links">
                    <Link
                        to="/admin/experiments"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-bl bg-bl-surface text-sm hover:border-bl-primary/40 transition-colors"
                        data-testid="admin-link-experiments"
                    >
                        A/B Experiments →
                    </Link>
                    <Link
                        to="/admin/audience-insights"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-bl bg-bl-surface text-sm hover:border-bl-primary/40 transition-colors"
                        data-testid="admin-link-audience-insights"
                    >
                        Audience Insights →
                    </Link>
                    <Link
                        to="/admin/subscription-analytics"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-bl bg-bl-surface text-sm hover:border-bl-primary/40 transition-colors"
                        data-testid="admin-link-subscription-analytics"
                    >
                        Subscription Analytics →
                    </Link>
                    <Link
                        to="/admin/calculators"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-bl bg-bl-surface text-sm hover:border-bl-primary/40 transition-colors"
                        data-testid="admin-link-calculators"
                    >
                        Calculator Engine →
                    </Link>
                </div>

                <SaveToGithubBanner />

                {/* Content Engine is the highest-leverage subsystem on this
                    dashboard — pinned to the top so admins see queue depth
                    and worker liveness before scrolling. The full library
                    page lives at /admin/content. */}
                <div className="mt-6" data-testid="admin-content-engine-section">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-semibold">
                            Content engine
                        </div>
                        <Link
                            to="/admin/content"
                            className="text-[11px] text-bl-muted hover:text-bl-text underline underline-offset-2"
                            data-testid="admin-content-engine-open-library"
                        >
                            Open content library →
                        </Link>
                    </div>
                    <ContentJobsTile />
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                        <PinRendererHealth />
                        <R2MirrorStatus />
                    </div>
                </div>

                <PreflightSmokeTest />

                <PipelineHealth />

                <HealthTripwiresPanel />

                <LaunchFunnel includeTest={includeTest} />

                <StuckUsersPanel />

                <LeadCapturesPanel />

                <TestFeedbackScrubPanel />

                <UrlSourcesPanel />

                <WcagAuditPanel />

                <RestoreFromR2 />

                <HeroRegeneration />

                <DayZeroReset />

                <Tabs defaultValue="content-engine" className="mt-12">
                    <TabsList className="bg-bl-surface border border-bl flex-wrap h-auto">
                        <TabsTrigger value="content-engine" data-testid="admin-tab-content-engine">Content Engine</TabsTrigger>
                        <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
                        <TabsTrigger value="uploads" data-testid="admin-tab-uploads">Uploads</TabsTrigger>
                        <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
                        <TabsTrigger value="txns" data-testid="admin-tab-txns">Transactions</TabsTrigger>
                        <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Contacts</TabsTrigger>
                        <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Quotes</TabsTrigger>
                        <TabsTrigger value="deletions" data-testid="admin-tab-deletions">Deletions</TabsTrigger>
                        <TabsTrigger value="stripe" data-testid="admin-tab-stripe">Stripe Events</TabsTrigger>
                        <TabsTrigger value="calibration" data-testid="admin-tab-calibration">Calibration</TabsTrigger>
                        <TabsTrigger value="optin" data-testid="admin-tab-optin">Email Opt-ins</TabsTrigger>
                        <TabsTrigger value="newsletter-stats" data-testid="admin-tab-newsletter-stats">Newsletter Stats</TabsTrigger>
                        <TabsTrigger value="cms" data-testid="admin-tab-cms">CMS</TabsTrigger>
                        <TabsTrigger value="support" data-testid="admin-tab-support" className="relative">
                            Support
                            {supportOutstanding.total > 0 && (
                                <span
                                    className="ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold leading-none border bg-[var(--bl-rust-tint)] border-[var(--bl-rust)] text-[var(--bl-rust)]"
                                    title={
                                        `${supportOutstanding.tickets_open} open ticket(s) · ${supportOutstanding.contacts_unread} unread contact message(s)`
                                    }
                                    data-testid="admin-tab-support-badge"
                                >
                                    {supportOutstanding.total}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="support-settings" data-testid="admin-tab-support-settings">Support Settings</TabsTrigger>
                        <TabsTrigger value="feedback" data-testid="admin-tab-feedback" className="relative">
                            Feedback
                            {triageCount > 0 && (
                                <span
                                    className={
                                        "ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold leading-none border "
                                        + (highSeverityCount > 0
                                            ? "bg-[var(--bl-rust-tint)] border-[var(--bl-rust)] text-[var(--bl-rust)]"
                                            : "bg-[var(--bl-amber-tint)] border-[var(--bl-amber)] text-[var(--bl-amber)]")
                                    }
                                    title={
                                        `${triageCount} aging feedback item(s) — ${highSeverityCount} high-severity`
                                    }
                                    data-testid="admin-tab-feedback-badge"
                                >
                                    {triageCount}
                                    {highSeverityCount > 0 && " !"}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="tools" data-testid="admin-tab-tools">Tools</TabsTrigger>
                        <TabsTrigger value="openai" data-testid="admin-tab-openai">OpenAI Usage</TabsTrigger>
                        <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>
                    </TabsList>

                    <TabsContent value="users" className="mt-5">
                        <Table
                            testid="admin-users-table"
                            rows={users}
                            columns={[
                                { key: "email", label: "Email" },
                                { key: "name", label: "Name" },
                                { key: "auth_provider", label: "Provider" },
                                { key: "role", label: "Role" },
                                { key: "persona", label: "Persona" },
                                {
                                    key: "created_at",
                                    label: "Created",
                                    render: (v) => (
                                        <span className="font-mono-bl text-xs">
                                            {v ? new Date(v).toLocaleString() : "—"}
                                        </span>
                                    ),
                                },
                            ]}
                        />
                    </TabsContent>
                    <TabsContent value="uploads" className="mt-5">
                        <Table
                            testid="admin-uploads-table"
                            rows={uploads}
                            columns={[
                                { key: "filename", label: "File" },
                                { key: "status", label: "Status" },
                                { key: "extraction_status", label: "Extraction" },
                                { key: "page_count", label: "Pages" },
                                { key: "user_id", label: "User" },
                                {
                                    key: "actions",
                                    label: "",
                                    render: (_v, r) =>
                                        r.status === "failed" ? (
                                            <button
                                                onClick={() => handleRetryExtraction(r.upload_id)}
                                                className="p-1.5 text-bl-muted hover:text-bl-primary"
                                                data-testid={`admin-retry-${r.upload_id}`}
                                                title="Mark for retry"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                            </button>
                                        ) : null,
                                },
                            ]}
                        />
                    </TabsContent>
                    <TabsContent value="reports" className="mt-5">
                        <Table
                            testid="admin-reports-table"
                            rows={reports}
                            columns={[
                                { key: "property_address", label: "Address" },
                                { key: "tier", label: "Tier" },
                                { key: "status", label: "Status" },
                                { key: "issue_count", label: "Issues" },
                                { key: "user_id", label: "User" },
                                {
                                    key: "actions",
                                    label: "",
                                    render: (_v, r) => (
                                        <button
                                            onClick={() => handleRegenerate(r.report_id)}
                                            className="p-1.5 text-bl-muted hover:text-bl-primary"
                                            data-testid={`admin-regen-${r.report_id}`}
                                            title="Regenerate"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                        </button>
                                    ),
                                },
                            ]}
                        />
                    </TabsContent>
                    <TabsContent value="txns" className="mt-5">
                        <Table
                            testid="admin-txns-table"
                            rows={txns}
                            columns={[
                                { key: "product_id", label: "Product" },
                                { key: "amount", label: "Amount" },
                                { key: "currency", label: "Cur" },
                                { key: "status", label: "Status" },
                                { key: "payment_status", label: "Payment" },
                                { key: "user_id", label: "User" },
                            ]}
                        />
                    </TabsContent>
                    <TabsContent value="contacts" className="mt-5">
                        <Table
                            testid="admin-contacts-table"
                            rows={contacts}
                            columns={[
                                { key: "name", label: "Name" },
                                { key: "email", label: "Email" },
                                { key: "subject", label: "Subject" },
                                { key: "message", label: "Message" },
                                {
                                    key: "status",
                                    label: "Status",
                                    render: (status, row) => {
                                        const isOpen = !status || status === "new" || status === "open";
                                        return (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        const next = isOpen ? "resolved" : "new";
                                                        await adminMarkContactStatus(row.message_id, next);
                                                        toast.success(
                                                            next === "resolved"
                                                                ? "Marked resolved"
                                                                : "Marked new",
                                                        );
                                                        refetchAll();
                                                        // refresh the badge immediately
                                                        try {
                                                            const r = await adminSupportOutstandingCount({ include_test: includeTest });
                                                            setSupportOutstanding({
                                                                tickets_open: r?.tickets_open || 0,
                                                                contacts_unread: r?.contacts_unread || 0,
                                                                total: r?.total || 0,
                                                            });
                                                        } catch { /* non-blocking */ }
                                                    } catch (e) {
                                                        toast.error(
                                                            e?.response?.data?.detail || "Update failed",
                                                        );
                                                    }
                                                }}
                                                className={
                                                    "px-2 py-1 rounded border text-[11px] "
                                                    + (isOpen
                                                        ? "bg-[var(--bl-rust-tint)] border-[var(--bl-rust)] text-[var(--bl-rust)] hover:bg-[var(--bl-rust)] hover:text-white"
                                                        : "bg-bl-bg border-bl text-bl-muted hover:bg-bl hover:text-white")
                                                }
                                                data-testid={`contact-mark-${isOpen ? "resolved" : "new"}-${row.message_id}`}
                                            >
                                                {isOpen ? "Mark resolved" : "Resolved · undo"}
                                            </button>
                                        );
                                    },
                                },
                            ]}
                        />
                    </TabsContent>
                    <TabsContent value="quotes" className="mt-5">
                        <QuotesAdmin
                            quotes={quotes}
                            counts={quoteCounts}
                            filter={quoteFilter}
                            onFilterChange={setQuoteFilter}
                            onRefresh={refetchAll}
                        />
                    </TabsContent>
                    <TabsContent value="deletions" className="mt-5">
                        <DeletionRequestsAdmin />
                    </TabsContent>
                    <TabsContent value="stripe" className="mt-5">
                        <StripeEventsAdmin />
                    </TabsContent>
                    <TabsContent value="calibration" className="mt-5">
                        <CostCalibrationAdmin />
                    </TabsContent>
                    <TabsContent value="optin" className="mt-5">
                        <p className="mb-3 text-bl-muted">
                            Total opted-in:{" "}
                            <span className="font-mono-bl text-bl">
                                {optins.count}
                            </span>
                        </p>
                        <Table
                            testid="admin-optin-table"
                            rows={optins.opted_in}
                            columns={[
                                { key: "email", label: "Email" },
                                { key: "name", label: "Name" },
                                { key: "persona", label: "Persona" },
                                {
                                    key: "created_at",
                                    label: "Joined",
                                    render: (v) =>
                                        v
                                            ? new Date(v).toLocaleDateString()
                                            : "—",
                                },
                            ]}
                        />
                    </TabsContent>
                    <TabsContent value="newsletter-stats" className="mt-5">
                        <NewsletterConversionDashboard />
                    </TabsContent>
                    <TabsContent value="cms" className="mt-5">
                        <CmsArticles />
                    </TabsContent>
                    <TabsContent value="content-engine" className="mt-5">
                        <AdminContentPackages />
                    </TabsContent>
                    <TabsContent value="support" className="mt-5">
                        <SupportInbox />
                    </TabsContent>
                    <TabsContent value="support-settings" className="mt-5">
                        <SupportSettings />
                    </TabsContent>
                    <TabsContent value="feedback" className="mt-5">
                        <AdminFeedback />
                    </TabsContent>
                    <TabsContent value="tools" className="mt-5">
                        <AdminTools />
                    </TabsContent>
                    <TabsContent value="openai" className="mt-5">
                        <OpenAIUsage />
                    </TabsContent>
                    <TabsContent value="audit" className="mt-5">
                        <Table
                            testid="admin-audit-table"
                            rows={auditLogs}
                            columns={[
                                {
                                    key: "created_at",
                                    label: "When",
                                    render: (v) => (
                                        <span className="font-mono-bl text-xs">
                                            {v ? new Date(v).toLocaleString() : "—"}
                                        </span>
                                    ),
                                },
                                { key: "actor_email", label: "Actor" },
                                { key: "action", label: "Action" },
                                { key: "target_type", label: "Target" },
                                { key: "target_id", label: "Id" },
                                {
                                    key: "details",
                                    label: "Details",
                                    render: (v) => (
                                        <span className="text-xs text-bl-muted">
                                            {v ? JSON.stringify(v).slice(0, 80) : "—"}
                                        </span>
                                    ),
                                },
                            ]}
                        />
                    </TabsContent>
                </Tabs>
            </section>
        </PublicLayout>
    );
}
